import { Viewer } from '../../viewer/viewer';
import { PointoramaWallSketch } from '../PointoramaWallSketch';
import { OpeningAnnotation } from '../PointoramaAnnotation';
import {
  getAnnotationNameProperty,
  getAreaPointsProperty,
  getOpeningKindProperty,
  getOpeningDoorTypeProperty,
  getOpeningHingeAtStartProperty,
  getOpeningSwingPositiveProperty,
  getOpeningRollableProperty,
  getOpeningWallSketchEdgeIdProperty,
  getOpeningSketchSourceOpeningIdProperty,
} from '@pointorama/potree-shared';
import type { OpeningKind, DoorGlyphType, WindowGlyphType } from 'shared-types';

/**
 * Parameters describing a new OPENING_SKETCH to be created, derived from a
 * floorplan OPENING projected onto a wall-sketch edge. Dispatched to the React
 * layer (which owns annotation creation) via the `request_opening_sketch` event.
 */
export interface OpeningSketchRequest {
  /** Optional explicit annotation id — set when re-creating a deleted opening
   *  (undo/redo) so it keeps its identity; otherwise the handler mints one. */
  identifier?: string;
  groupIdentifier: string;
  wallEdgeId: string;
  offset: number;
  length: number;
  lowerBound: number;
  /** Vertical size ABOVE the ground offset (m). Head Z = lowerBound + height. */
  height: number;
  /** Non-negative distance the glyph sits inward from the outer wall face on its
   *  side (Side = swingPositive). Converted to a signed centerline offset at
   *  glyph time (see signedPerpFromInset). */
  inset: number;
  kind: OpeningKind;
  doorType: DoorGlyphType;
  /** Window symbol (built-in STANDARD or an organisation window); absent = STANDARD. */
  windowType?: WindowGlyphType;
  hingeAtStart: boolean;
  swingPositive: boolean;
  rollable: boolean;
  sourceOpeningId: string;
  /** Display name to carry over from the source OPENING (a user rename like
   *  "Front door" must survive conversion). Empty/absent → the creation
   *  handler falls back to the kind default (Door / Window). */
  name?: string;
}

/**
 * Replicate every floorplan OPENING on a floor into an OPENING_SKETCH on the
 * floor's wall sketch — the "transform floor objects into their sketch
 * equivalents" step of a floor→sketch conversion.
 *
 * For each OPENING whose center projects onto one of the wall sketch's edges:
 *   - offset/length: project the opening's along-wall extent onto the edge
 *   - lower/upper bound: the opening polygon's Z extent
 *   - glyph params (kind/door type/swing/rollable): copied verbatim
 *   - name: copied verbatim (user renames survive the conversion)
 *   - source link: openingsketch_source_opening_id = the OPENING's identifier
 *
 * Idempotent: an OPENING already replicated (a scene OpeningSketch or cached
 * OPENING_SKETCH annotation carries its id as source) is skipped, so re-running
 * the conversion never duplicates. The original OPENING is left untouched (the
 * floorplan persona keeps using it).
 *
 * Annotation creation itself happens in React; this dispatches one
 * `request_opening_sketch` event per new opening sketch.
 */
export function replicateOpeningSketchesForFloor(viewer: Viewer, wallSketch: PointoramaWallSketch): void {
  const groupIdentifier = wallSketch.groupIdentifier;
  if (!groupIdentifier) return;

  // Already-replicated source ids (from live scene objects + cached annotations).
  const alreadyReplicated = new Set<string>();
  for (const os of viewer.scene.openingSketches) {
    try {
      const src = getOpeningSketchSourceOpeningIdProperty(os.identifier).value;
      if (src) alreadyReplicated.add(src);
    } catch {
      // property service not ready — treat as not replicated
    }
  }

  const edges = wallSketch.getEdges();
  if (edges.length === 0) return;

  const wallRoomIds = new Set<string>();
  for (const e of edges) for (const rid of e.roomIds) wallRoomIds.add(rid);

  for (const m of viewer.scene.measurements) {
    if (!(m instanceof OpeningAnnotation)) continue;
    if (m.groupIdentifier !== groupIdentifier) continue;
    if (alreadyReplicated.has(m.identifier)) continue;

    let pts;
    let kind;
    let doorType;
    let hingeAtStart;
    let swingPositive;
    let rollable;
    let preferredEdgeId = '';
    let name = '';
    try {
      pts = getAreaPointsProperty(m.identifier).value;
      kind = getOpeningKindProperty(m.identifier).value;
      doorType = getOpeningDoorTypeProperty(m.identifier).value;
      hingeAtStart = getOpeningHingeAtStartProperty(m.identifier).value;
      swingPositive = getOpeningSwingPositiveProperty(m.identifier).value;
      rollable = getOpeningRollableProperty(m.identifier).value;
      preferredEdgeId = getOpeningWallSketchEdgeIdProperty(m.identifier).value || '';
      name = getAnnotationNameProperty(m.identifier).value || '';
    } catch {
      continue;
    }
    if (!pts || pts.length < 3) continue;

    const params = openingSketchParamsFromPolygon(
      wallSketch,
      pts.map((p) => p.position),
      preferredEdgeId ? [preferredEdgeId] : [],
      swingPositive,
    );
    if (!params) continue;

    const request: OpeningSketchRequest = {
      groupIdentifier,
      wallEdgeId: params.wallEdgeId,
      offset: params.offset,
      length: params.length,
      lowerBound: params.lowerBound,
      height: params.height,
      inset: params.inset,
      kind,
      doorType,
      hingeAtStart,
      swingPositive,
      rollable,
      sourceOpeningId: m.identifier,
      name,
    };
    alreadyReplicated.add(m.identifier);
    viewer.scene.dispatchEvent({ type: 'request_opening_sketch', value: request });
  }
}

/** Geometry-only parameter set of an OPENING_SKETCH derived from a polygon. */
export interface OpeningSketchGeometryParams {
  wallEdgeId: string;
  offset: number;
  length: number;
  /** Absolute world Z of the sill. */
  lowerBound: number;
  /** Vertical size above `lowerBound`. */
  height: number;
  inset: number;
}

/**
 * Map an opening POLYGON onto a wall sketch's parametric opening encoding:
 * pick the best edge (preferring `preferredEdgeIds`, e.g. the wall chain the
 * user focused — else the geometrically nearest edge to the centroid), project
 * the polygon's along-wall extent to `offset`/`length` (its wall-frame AABB, so
 * a non-rectangle collapses to its bounding rectangle), take the Z extent as
 * sill + height, and convert the mean perpendicular offset to the stored
 * `inset` on the `swingPositive` side. Null when nothing projects.
 */
export function openingSketchParamsFromPolygon(wallSketch: PointoramaWallSketch, points: { x: number; y: number; z: number }[], preferredEdgeIds: readonly string[], swingPositive: boolean): OpeningSketchGeometryParams | null {
  if (points.length < 3) return null;
  const edges = wallSketch.getEdges();
  if (edges.length === 0) return null;

  // Centroid + Z extent.
  let cx = 0;
  let cy = 0;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of points) {
    cx += p.x;
    cy += p.y;
    minZ = Math.min(minZ, p.z);
    maxZ = Math.max(maxZ, p.z);
  }
  cx /= points.length;
  cy /= points.length;

  const projection = projectOntoBestEdge(wallSketch, edges, preferredEdgeIds, { x: cx, y: cy }, points);
  if (!projection) return null;

  // Convert the measured signed centerline offset to the stored `inset`
  // (distance in from the outer wall face on the opening's side). Inverse of
  // signedPerpFromInset:  swing+ → inset = leftSize − perp;  swing− → inset =
  // rightSize + perp. Clamp to ≥0.
  const projEdge = wallSketch.getEdge(projection.edgeId);
  const leftSize = projEdge?.leftSize ?? 0.1;
  const rightSize = projEdge?.rightSize ?? 0.1;
  const inset = Math.max(0, swingPositive ? leftSize - projection.perpOffset : rightSize + projection.perpOffset);

  return {
    wallEdgeId: projection.edgeId,
    offset: projection.offset,
    length: projection.length,
    lowerBound: minZ === Infinity ? 0 : minZ,
    // `height` is stored relative to the ground offset (lower).
    height: minZ === Infinity || maxZ === -Infinity ? 0 : Math.max(0, maxZ - minZ),
    inset,
  };
}

interface EdgeProjection {
  edgeId: string;
  offset: number;
  length: number;
  perpOffset: number;
}

/**
 * Project the opening's along-wall extent + perpendicular distance onto the
 * best edge. Preferred edges are honoured unconditionally when any resolves:
 * with one preferred id (the explicit annotation link) that edge wins; with
 * several (a focused collinear wall chain) the chain member nearest the
 * centroid wins. Only when none resolves does the global nearest edge apply.
 */
function projectOntoBestEdge(
  wallSketch: PointoramaWallSketch,
  edges: ReturnType<PointoramaWallSketch['getEdges']>,
  preferredEdgeIds: readonly string[],
  center: { x: number; y: number },
  pts: { x: number; y: number }[],
): EdgeProjection | null {
  const preferred = new Set(preferredEdgeIds);

  type Candidate = { edge: (typeof edges)[number]; a: { x: number; y: number }; b: { x: number; y: number }; perpDist: number };
  let bestPreferred: Candidate | null = null;
  let bestAny: Candidate | null = null;
  for (const edge of edges) {
    const an = wallSketch.getNode(edge.startNodeId);
    const bn = wallSketch.getNode(edge.endNodeId);
    if (!an || !bn) continue;
    const a = { x: an.position.x, y: an.position.y };
    const b = { x: bn.position.x, y: bn.position.y };
    const perpDist = perpendicularDistanceToSegment(center, a, b);
    const candidate: Candidate = { edge, a, b, perpDist };
    if (preferred.has(edge.id) && (!bestPreferred || perpDist < bestPreferred.perpDist)) bestPreferred = candidate;
    if (!bestAny || perpDist < bestAny.perpDist) bestAny = candidate;
  }
  const best = bestPreferred ?? bestAny;
  if (!best) return null;

  const { a, b } = best;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-12) return null;
  const len = Math.sqrt(lenSq);
  const ux = dx / len;
  const uy = dy / len;
  // wallNorm = 90° CCW of (ux, uy), matching frameFromWallOpeningSketch.
  const normX = -uy;
  const normY = ux;

  // Project every polygon point onto the edge axis; offset/length span those.
  let minU = Infinity;
  let maxU = -Infinity;
  let perpSum = 0;
  for (const p of pts) {
    const rx = p.x - a.x;
    const ry = p.y - a.y;
    const u = rx * ux + ry * uy;
    minU = Math.min(minU, u);
    maxU = Math.max(maxU, u);
    perpSum += rx * normX + ry * normY;
  }
  if (minU === Infinity) return null;
  const length = Math.max(0, maxU - minU);
  const offset = minU;
  const perpOffset = perpSum / pts.length;

  return { edgeId: best.edge.id, offset, length, perpOffset };
}

/** Distance from point p to segment a→b (clamped to the segment). */
function perpendicularDistanceToSegment(p: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-12) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const fx = a.x + t * dx;
  const fy = a.y + t * dy;
  return Math.hypot(p.x - fx, p.y - fy);
}
